import React, { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import Badge from '@mui/material/Badge'
import { styled } from '@mui/material/styles'
import IconButton from '@mui/material/IconButton'
import { DialogContent, Typography, useMediaQuery } from '@mui/material'
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart'
import CartActions from './CartActions.js'
import CartLayout from './cartLayout.js'
import LS from '../../utils/localStorage'
import CartApi from '../../api/cartAPI.js'
import CONSTANTS from '../../constants'
import ReactGA from 'react-ga4'
import ReactPixel from 'react-facebook-pixel'
import { useLocation, useNavigate } from 'react-router-dom'
import moment from 'moment'
import VolumeDiscountBar from './volumeDiscountBar.js'
import CartSummary from './CartSummary.js'
import CartPanel from './CartPanel.js'

const StyledBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    right: -3,
    top: 13,
    border: `2px solid ${theme.palette.background.paper}`,
    padding: '0 4px',
    color: theme.palette.background.paper,
    backgroundColor: CONSTANTS.RED_HEX
  }
}))

const CartContainer = ({ purchaseByAdmin }) => {
  const windowLocation = useLocation()
  const navigate = useNavigate()

  const cartInv = useSelector((state) => state.cart.products)
  const volumeDiscountCount = useSelector((state) => state.cart.volumeDiscountCount)
  const pID = useSelector((state) => state.login.user._id)
  const stripeCustID = useSelector(
    (state) => state.login.user?.paymentMeta?.stripe?.stripeID
  )
  const customerUN = useSelector((state) => state.login.user?.userName)

  //Checks if user is admin or parent
  let user = useSelector((state) => {
    if (
      state.login.user.permissions && //checks if a user is logged in
      state.login?.user?.permissions[0] === 4 //checks if user is an admin
    ) {
      return state.user.selectedUser //gets user selected by admin
    } else {
      return state.login.user //gets logged in user
    }
  })
  const [numInCart, setNumInCart] = useState(0)
  const [showCart, setShowCart] = useState(false)
  const [total, setTotal] = useState(0)
  const [useAlternate, setUseAlternate] = useState(purchaseByAdmin || false)
  const [termsCheck, setTermsCheck] = useState(false)
  const [isCurrentPage, setIsCurrentPage] = useState(false)
  const [promoCodeInput, setPromoCodeInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [paymentType, setPaymentType] = useState('card')
  const [couponApplied, setCouponApplied] = useState({
    code: '',
    value: 0,
    isPercent: true
  })
  const [discount, setDiscount] = useState(0) // Bulk discount, always percentage
  const [promoCodeLoading, setPromoCodeLoading] = useState(false)

  const [view, setView] = useState(0)
  const [termsError, setTermsError] = useState(false)

  useEffect(() => {
    if (windowLocation.pathname === '/alternate-check-out') {
      setIsCurrentPage(true)
    } else {
      setIsCurrentPage(false)
    }
  }, [windowLocation])

  // Compares coupon to existing discount, returns true if coupon is greater
  const compareDiscount = (value, isPercent) => {
    if (
      (isPercent && total * value > discount * total) ||
      value > discount * total
    ) {
      return true
    }
    return false
  }

  useEffect(() => {
    if (volumeDiscountCount > 0) {
      let volumeDiscount = Math.min(volumeDiscountCount - 1, 4) * 0.05
      let couponDiscount = couponApplied.value
      setDiscount(Math.max(volumeDiscount, couponDiscount))
    } else {
      setDiscount(0)
    }
  }, [volumeDiscountCount, couponApplied])

  const [errors, setErrors] = useState({})
  const [achAuthorized, setAchAuthorized] = useState(true)

  // const styles = {
  //   cart: {
  //     padding: '0px 10px',
  //     color: isCurrentPage ? '#fff' : null
  //   }
  // }

  const updateTotal = (items) => {
    let tmp = 0
    items.forEach((item) => {
      tmp += item.priceInfo.unit_amount
    })
    setTotal(tmp / 100)
  }

  useEffect(() => {
    setNumInCart(cartInv.length)
    LS.setItem('cart', cartInv)
    updateTotal(cartInv)
    if (cartInv.length === 0) {
      setErrors({})
    }
  }, [cartInv])

  useEffect(() => {
    if (promoCodeInput === '' && errors['coupon']) {
      setErrors((prev) => {
        const { coupon, ...rest } = prev
        return rest
      })
    }
  }, [promoCodeInput])

  const handleAlternate = () => {
    setUseAlternate((prev) => !prev)
  }
  // TO DO: Decide how to handle coupon overriding
  // Handles coupon change
  const handlePromoCodeInput = (e) => {
    setPromoCodeInput(e.target.value)
  }

  // Handles verifying promo code valid
  const handlePromoCodeSubmit = async () => {
    setPromoCodeLoading(true)
    let resp = await CartApi.handlePromoCode({ promoCode: promoCodeInput })
    if (resp.status == 'success') {
      let couponValue = 0
      // One of these will be null
      let percentOff = resp.data.coupon.percent_off
      let amountOff = resp.data.coupon.amount_off
      let isPercent = true
      if (percentOff) {
        couponValue = percentOff / 100
      } else {
        couponValue = amountOff / 100
        isPercent = false
      }
      if (compareDiscount(couponValue, isPercent)) {
        setCouponApplied({
          code: promoCodeInput,
          value: couponValue,
          isPercent: isPercent
        })
        //TO DO: store coupon in redux store
        setPromoCodeInput('')
      } else {
        alert('Coupon is less than current discount')
      }
    } else {
      setErrors({ coupon: true })
    }
    setPromoCodeLoading(false)
  }

  const handlePromoCodeRemove = () => {
    setCouponApplied({ code: '', value: 0, isPercent: true })
    setDiscount(0)
  }

  const handleErrors = () => {
    let tmp = {}
    cartInv.forEach((item) => {
      if (item.student === 'default') {
        tmp[item['cartID']] = 'defaultSelected'
        setLoading(false)
      } else if (
        cartInv.filter(
          (elem) =>
            elem.student === item.student && elem.stripeID === item.stripeID
        ).length > 1
      ) {
        tmp[item['cartID']] = 'doubleStudent'
        setLoading(false)
      }
    })
    if (
      cartInv.length === 1 &&
      new moment(cartInv[0].metadata.startDate).subtract(2, 'days') <
        new moment()
    ) {
      setAchAuthorized(false)
    } else {
      setAchAuthorized(true)
    }
    return tmp
  }
  const handleSubmit = async () => {
    setLoading(true)
    ReactGA.event({
      category: 'button',
      action: 'Cart Checkout'
    })

    ReactPixel.track('InitiateCheckout', {
      content_category: 'Cart_Checkout'
    })

    let errors = handleErrors()
    setErrors(errors)

    if (cartInv.length !== 0 && Object.keys(errors).length === 0) {
      if (useAlternate) {
        setShowCart(false)
        setLoading(false)
        navigate('/alternate-check-out')
      } else {
        let errorsTmp = {}
        // temp bug fix of passing the un instead of contact email, need to update DB
        let resp = await CartApi.createCheckout({
          cart: cartInv,
          p: { rID: pID, sID: stripeCustID, email: customerUN },
          coupon: couponApplied.code,
          volumeDiscountCount: volumeDiscountCount,
          paymentType: paymentType
        })
        if (resp.status === 'fail') {
          alert(resp.message)
          setLoading(false)
          if (resp.reason === 'invalidCoupon') {
            errorsTmp.coupon = true
          } else if (resp.reason === 'enrollmentError') {
            cartInv.forEach((item) => {
              if (resp.taken.indexOf(item.id) > -1) {
                errorsTmp[item['cartID']] = 'capacityFull'
              } else if (
                resp.enrolled.filter((enroll) => enroll['id'] === item.id)[0]
              ) {
                errorsTmp[item['cartID']] = 'alreadyEnrolled'
              }
            })
          }
          setErrors(errorsTmp)
        } else {
          setLoading(false)
          setErrors({})
        }
      }
    }
  }

  const handleClick = () => {
    setShowCart(true)
  }

  const mobile = useMediaQuery((theme) => theme.breakpoints.down('md'))

  const handleClose = () => {
    setShowCart(false)
    setPromoCodeInput('')
    setLoading(false)
    setTermsCheck(false)
  }
  const styles = {
    actions: {
      display: 'flex',
      justifyContent: 'space-between'
    },
    helpText: {
      color: '#d32f2f',
      fontSize: 12
    },
    tempWeb: { minWidth: '80%' },
    dialogTitle: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      '&.MuiDialogTitle-root': {}
    },
    dialogContent: {
      overflowY: 'auto',
      display: 'grid',
      gridTemplateColumns: '60% 40%',
      // gridTemplateRows: 'auto auto',
      '&.MuiDialogContent-root': { padding: '0px' },
      minWidth: '50vw'
    },
    promoCodeBox: {
      textAlign: 'left',
      marginBottom: '10px'
    },
    termsBox: {
      display: 'flex',
      justifyContent: 'flex-start',
      alignItems: 'flex-end'
    },
    termsCheckbox: {
      '.MuiSvgIcon-root': { color: 'red' }
    },
    dialogActions: {
      justifyContent: 'flex-end',
      width: '100%',
      '&.MuiDialogActions-root': { padding: '8px 24px 16px 24px' }
    },
    errorText: {
      color: '#d32f2f'
    },
    loadingBox: {
      display: 'flex',
      alignItems: 'center'
    },
    lineItem: {
      display: 'flex',
      justifyContent: 'space-between'
    }
  }

  const handleDisable = (value) => {
    if (useAlternate) {
      return value !== 'alternate_payment'
    }
    if (!achAuthorized) {
      return value == 'us_bank_account'
    }
  }

  const handleTermsCheck = () => {
    setTermsCheck(!termsCheck)
    setTermsError(false)
  }
  //TODO: Rename
  const handleChange = (value) => {
    setPaymentType(value)
  }

  return (
    <>
      <IconButton
        aria-label='cart'
        onClick={isCurrentPage ? null : handleClick}
        sx={{
          color: '#000',
          backgroundColor: isCurrentPage ? 'secondary.main' : null,
          borderRadius: '10px',
          '& .MuiTouchRipple-root .MuiTouchRipple-child': {
            borderRadius: '10px'
          },
          '&:hover': {
            backgroundColor: isCurrentPage ? 'secondary.main' : null
          }
        }}
      >
        <StyledBadge
          anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
          badgeContent={numInCart}
        >
          <ShoppingCartIcon
            sx={{ color: isCurrentPage ? '#fff' : 'secondary.main' }}
            fontSize='small'
          />
        </StyledBadge>
        {mobile ? (
          <></>
        ) : (
          <Typography variant='body1' sx={styles.cart}>
            Cart
          </Typography>
        )}
      </IconButton>
      <CartLayout showCart={showCart} handleClose={handleClose} mobile={mobile}>
        <DialogContent sx={styles.dialogContent}>
          <VolumeDiscountBar
            discount={discount}
            couponApplied={couponApplied}
          />
          <CartPanel
            view={view}
            cartInv={cartInv}
            discount={discount}
            errors={errors}
            useAlternate={useAlternate}
            handleChange={handleChange}
            paymentType={paymentType}
            handleDisable={handleDisable}
            user={user}
            purchaseByAdmin={purchaseByAdmin}
            termsCheck={termsCheck}
            handleTermsCheck={handleTermsCheck}
            termsError={termsError}
            handleErrors={handleErrors}
            setErrors={setErrors}
            mobile={mobile}
            couponApplied={couponApplied}
          />
          <CartSummary
            user={user}
            handlePromoCodeInput={handlePromoCodeInput}
            promoCodeInput={promoCodeInput}
            errors={errors}
            promoCodeLoading={promoCodeLoading}
            handlePromoCodeSubmit={handlePromoCodeSubmit}
            handlePromoCodeRemove={handlePromoCodeRemove}
            couponApplied={couponApplied}
            discount={discount}
            total={total}
            mobile={mobile}
          />
        </DialogContent>
        <CartActions
          cartInv={cartInv}
          user={user}
          view={view}
          setView={setView}
          termsError={termsError}
          setTermsError={setTermsError}
          termsCheck={termsCheck}
          handleClose={handleClose}
          handleSubmit={handleSubmit}
          errors={errors}
          setErrors={setErrors}
          loading={loading}
          handleErrors={handleErrors}
        />
      </CartLayout>
    </>
  )
}

export default CartContainer
